import { CustomMouseEvent } from '../controls';
import { SceneCoordinate } from '../projections';
import { ChangeDetector, getPointScale } from '../utilities';
import {
  MeasurementArea,
  MeasurementVolume,
  MeasurementController,
  Measurement,
  HeightMeasurement,
  MeasurementLine,
  MeasurementType,
  StationMeasurement
} from '.';
import {
  Mesh,
  Scene,
  SphereGeometry,
  MeshBasicMaterial,
  LineBasicMaterial,
  Line,
  BufferGeometry,
  Vector3,
  Sphere
} from 'three';
import { clearViewerTooltip, setViewerTooltip } from '../viewer';
import { RayCaster } from '../ray-caster';
import { t } from '../../localization';

interface MeasureObject {
  id: number
  lines: Line[]
  points: MeasureEndpoint[]
  labels: any[]
}

const alternateLabelColor = "lightgreen";

export class MeasureEndpoint extends Mesh {
  public measurementVertex;
  public measurementID;

  constructor(geometry, material, index, id) {
    super(geometry, material);
    this.measurementVertex = index;
    this.measurementID = id;
  }

  /** Custom hit detection based on bounding sphere */
  raycast(raycaster, intersects) {
    const sphere = new Sphere();
    sphere.copy(this.geometry.boundingSphere);
    sphere.applyMatrix4(this.matrixWorld);

    const ray = raycaster.ray;
    if (!ray.intersectsSphere(sphere)) return;

    const origin = raycaster.ray.origin;
    const distance = origin.distanceTo(this.position);

    intersects.push({
      distance: distance,
      point: null,
      object: this
    });
  }
}

export class TemporaryEndpoint extends Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }
}

export class MeasurementSceneView {
  private controller: MeasurementController;
  private scene: Scene;

  private sphereBuffer = 1.2;
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);
  private sphereMaterialStandard = new MeshBasicMaterial({color: 0xffffff});
  private sphereMaterialHover = new MeshBasicMaterial({color: 0xffcc33});
  private lineMaterialStandard = new LineBasicMaterial({color: 0x008B8B});
  private lineMaterialHeight = new LineBasicMaterial({color: 0xFF7474});
  private lineMaterialHover = new LineBasicMaterial({color: 0xffcc33});

  private measurements: MeasureObject[] = [];
  private temporaryEndpoints: TemporaryEndpoint[] = [];
  private temporaryLines = [];
  private temporaryLabels = [];
  public hoveredPoint: TemporaryEndpoint = null;
  public dragObject: MeasureEndpoint = null;
  private dragValid = false;

  constructor(controller, scene) {
    this.controller = controller;
    this.scene = scene;
  }

  get viewer() {
    return this.controller.viewer;
  }

  get camera() {
    return this.viewer.camera;
  }

  get dragging() {
    return !!this.dragObject;
  }

  get dragTooltip() {
    return [
      t('measure.click-and-hold-to-move-vertex')
    ];
  }

  get restrictedTooltip() {
    return [
      t('measure.unable-to-move-this-measurement-type')
    ];
  }

  get formatter() {
    return this.controller.formatter;
  }

  get measurementGroup() {
    return this.controller.measurementGroup;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (!event.mouseMoved && !event.mouseDown) {
      this.checkEndpointSelected(event);
    }

    if (this.controller.measuring) {
      let measurement = this.controller.activeMeasurement;
      let coordinate = this.controller.getCoordinate(event);
      this.addActiveEndpoint(measurement, coordinate);
    }

    if (this.dragObject) return true;

    this.controller.updateMeasureText();
    return !this.controller.disabled;
  }

  onMouseDrag(event: CustomMouseEvent) {
    if (!this.dragObject) return false;

    if (event.mouseDown && event.isLeftClick) {
      let coordinate = this.controller.getCoordinate(event);
      this.updateMeasureEndpoint(coordinate);
      return true;
    }

    return false;
  }

  updateMeasureEndpoint(coordinate) {
    if (!this.dragObject) return;
    if (!coordinate) return;

    let id = this.dragObject.measurementID;
    let vertexIndex = this.dragObject.measurementVertex;
    let measurement = this.measurementGroup.getByID(id);

    if (!measurement) return;

    if (measurement instanceof StationMeasurement) {
      this.setRestrictedPointTooltip();
      this.dragValid = false;
      return;
    }
    if (measurement instanceof MeasurementVolume) {
      measurement.stopVolumeCalculation();
    }

    this.dragValid = true;

    // Update our measurement vertex value
    let vertex = new SceneCoordinate(coordinate).toGeocentric();
    measurement.vertices[vertexIndex] = vertex;

    // Clear existing measurement and redraw
    this.removeByID(measurement.id);
    this.render(measurement);
  }

  measureGrabFinished() {
    if ((!this.dragObject) || (!this.dragValid)) return;

    this.controller.refreshMeasurementList();

    this.measurementGroup.measurements.forEach(measurement => {
      if (measurement instanceof MeasurementVolume) {
        if (measurement.calculated) return;
        measurement.calculateVolume();
      }
    });
  }

  checkEndpointSelected(event) {
    let controller = this.controller;
    if (!controller.editable) return;
    if (controller.measuring) return;

    let targets = this.scene.children
      .filter(x => x instanceof MeasureEndpoint);

    let raycaster = new RayCaster(this.viewer, event);
    let objects = raycaster.intersectObjects(targets, {
      minDistance: 0.10,
      sortByRay: true
    });

    let draggable = objects.length > 0;
    let dragObject = draggable ? objects[0].object : null;
    this.setDragObject(dragObject);
  }

  setDragObject(dragObject) {
    // Image-only measurements can't be dragged
    if (this.controller.isImageOnly) {
      dragObject = null;
    }

    this.dragObject = dragObject;

    let draggable = dragObject ? true : false;
    let controls = this.controller.controls;
    controls.setMovementState(!draggable);

    if (dragObject) {
      this.setPointGrabTooltip();
    }
  }

  setPointGrabTooltip() {
    setViewerTooltip(this.dragTooltip);
  }

  setRestrictedPointTooltip() {
    setViewerTooltip(this.restrictedTooltip);
  }

  setTooltipText(measurement: MeasurementType, morepoints = false) {
    if (!measurement) {
      clearViewerTooltip();
      return;
    }

    let tooltipText = [] as string[];

    if (morepoints) {
      let endTooltipCount = measurement.minVertexRequired - 1;
      endTooltipCount = Math.max(2, endTooltipCount);

      if (measurement.vertices.length < endTooltipCount) {
        tooltipText = this.controller.isImageOnly
          ? measurement.moreTooltipMulti
          : measurement.moreTooltip;
      } else {
        tooltipText = this.controller.isImageOnly
          ? measurement.endTooltipMulti
          : measurement.endTooltip;
      }
    } else {
      tooltipText = this.controller.isImageOnly
        ? measurement.startTooltipMulti
        : measurement.startTooltip;
    }

    setViewerTooltip(tooltipText);
  }

  setPointScale(mesh) {
    const scale = getPointScale(this.camera, mesh);
    mesh.scale.set(scale, scale, scale);
    mesh.geometry.computeBoundingSphere();
    mesh.geometry.boundingSphere.radius *= this.sphereBuffer;
  }

  addTemporaryPoint(coordinate) {
    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.temporaryEndpoints.push(sphere);
  }

  addActiveEndpoint(measurement, coordinate) {
    if (coordinate === null) return;

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.hoveredPoint = sphere;

    this.removeObjects(this.temporaryLines);
    this.removeLabels(this.temporaryLabels);

    this.temporaryLines = [];
    this.temporaryLabels = [];

    // Draw new lines and area
    let vertices = [...this.temporaryEndpoints, this.hoveredPoint]
      .map(point => point.position);

    const temporaryPoint = new SceneCoordinate(
      this.hoveredPoint.position);

    this.temporaryLines = this.drawLines(
      measurement, vertices, temporaryPoint);

    this.temporaryLabels = this.drawLabels(
      measurement, this.temporaryLines);
  }

  createEndpoint(coordinates, index, id) {
    let sphere = new MeasureEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard, index, id);

    sphere.position.copy(coordinates);
    this.setPointScale(sphere);

    return sphere;
  }

  createTempEndpoint(coordinate) {
    let sphere = new TemporaryEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard);

    sphere.position.copy(coordinate);
    this.setPointScale(sphere);

    return sphere;
  }

  createLine(start: Vector3, end: Vector3, useAlternate) {
    const vertices = [start.clone(), end.clone()];
    const geometry = new BufferGeometry().setFromPoints(vertices);
    const material = useAlternate
      ? this.lineMaterialHeight
      : this.lineMaterialStandard;

    let line = new Line(geometry, material);
    line.userData.defaultMaterial = material;

    return line;
  }

  setHover(id, hover) {
    let measurement = this.getByID(id);
    if (!measurement) return;

    for (let object of measurement.lines) {
      const lineMaterial = hover
        ? this.lineMaterialHover
        : object.userData.defaultMaterial;

      object.material = lineMaterial as LineBasicMaterial;
      object.material.needsUpdate = true;
    }

    for (let object of measurement.points) {
      const pointMaterial = hover
        ? this.sphereMaterialHover
        : this.sphereMaterialStandard;

      object.material = pointMaterial;
      object.material.needsUpdate = true;
    }
  }

  drawPoints(measurement: Measurement, vertices: Vector3[]): MeasureEndpoint[] {
    let points = [];

    for (let i=0; i<vertices.length; i++) {
      const posScene = vertices[i];
      const point = this.createEndpoint(
        posScene, i, measurement.id);
      this.scene.add(point);
      points.push(point);
    };

    return points;
  }

  drawLines(measurement: Measurement, vertices: Vector3[], temporaryPoint: SceneCoordinate = null): Line[] {
    let lines = [];

    // Convert to closed polygon for area types + calculate surface
    const hasEnoughPoints = vertices.length >= 3;
    if ((measurement instanceof MeasurementArea) && hasEnoughPoints) {
      vertices.push(vertices[0]);
      measurement.generateSurface(temporaryPoint);
    }

    // Draw standard lines
    for (let i=0; i<vertices.length-1; i++) {
      const vertexA = vertices[i];
      const vertexB = vertices[i+1];
      const line = this.createLine(vertexA, vertexB, false);

      this.scene.add(line);
      lines.push(line);
    }

    // Additional lines for height measurement
    if (measurement instanceof HeightMeasurement) {
      let firstPointHigher = vertices[0].z > vertices[1].z;
      let vertex0 = firstPointHigher ? vertices[0] : vertices[1];
      let vertex1 = firstPointHigher ? vertices[1] : vertices[0];
      let vertex2 = new SceneCoordinate([vertex0.x, vertex0.y, vertex1.z]);

      let line0 = this.createLine(vertex1, vertex2, true);
      this.scene.add(line0);
      lines.push(line0);

      let line1 = this.createLine(vertex2, vertex0, true);
      this.scene.add(line1);
      lines.push(line1);
    }

    return lines;
  }

  drawLabels(measurement: Measurement, lines: Line[]) {
    let totalLength = 0;
    let labels = [];

    // Draw standard length labels
    lines.forEach((line, index) => {
      const positions = line.geometry.attributes.position;
      const P1 = new Vector3().fromBufferAttribute(positions, 0);
      const P2 = new Vector3().fromBufferAttribute(positions, 1);

      const length = P1.distanceTo(P2);
      const message = this.formatter.formatLength(length);

      totalLength += length;

      const midpoint = new Vector3()
        .addVectors(P1, P2)
        .divideScalar(2.0);

      const position = new SceneCoordinate(midpoint)
        .toDataProjection()
        .toArray();

      const isHeight = measurement instanceof HeightMeasurement;
      const lastLines = index >= lines.length-2;

      const style = (isHeight && lastLines)
        ? {color: alternateLabelColor}
        : null;

      const label = this.viewer.addSceneLabel(
        message, position, null, style);

      labels.push(label);
    });

    let objectMessage;
    let objectPosition;

    // Total line length
    if (measurement instanceof MeasurementLine) {
      const line = lines[lines.length - 1];
      const positions = line.geometry.attributes.position;
      const endPoint = new Vector3().fromBufferAttribute(positions, 1);

      objectMessage = this.formatter.formatLength(totalLength);
      objectPosition = new SceneCoordinate(endPoint)
        .toDataProjection()
        .toArray();
    }

    // Total area / volume
    const hasEnoughLines = lines.length >= 3;
    if ((measurement instanceof MeasurementArea) && hasEnoughLines) {
      const midpoint = new Vector3();
      lines.forEach(line => {
        const positions = line.geometry.attributes.position;
        const vertex = new Vector3().fromBufferAttribute(positions, 1);
        midpoint.add(vertex);
      });
      midpoint.divideScalar(lines.length);

      if (measurement instanceof MeasurementVolume) {
        if (measurement.calculated) {
          objectMessage = this.formatter.formatVolume(measurement);
        }
      } else {
        const {area, valid} = measurement.area;

        objectMessage = valid
          ? this.formatter.formatArea(area)
          : t('measure.invalid-area');
      }

      objectPosition = new SceneCoordinate(midpoint)
        .toDataProjection()
        .toArray();
    }

    if (objectPosition && objectMessage) {
      const style = {
        color: alternateLabelColor
      };

      const label = this.viewer.addSceneLabel(
        objectMessage, objectPosition, null, style);

      labels.push(label);
    }

    this.viewer.updateSceneLabels();

    return labels;
  }

  render(measurement) {
    let oldMeasurement = this.getByID(measurement.id);
    this.removeMeasurement(oldMeasurement);

    const vertices = measurement.vertices
      .map(vertex => vertex.toScene());

    const points = this.drawPoints(measurement, vertices);
    const lines = this.drawLines(measurement, vertices);
    const labels = this.drawLabels(measurement, lines);

    this.measurements.push({
      id: measurement.id,
      labels,
      lines,
      points
    });
  }

  removeObjects(objects) {
    objects.forEach(object => {
      this.scene.remove(object);
    });
  }

  removeLabels(labels) {
    labels.forEach(label => {
      this.viewer.removeSceneLabel(label);
    });

    this.viewer.updateSceneLabels();
  }

  removeTemporaryObjects() {
    this.removeObjects(this.temporaryEndpoints);
    this.removeObjects(this.temporaryLines);
    this.removeLabels(this.temporaryLabels);

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    this.temporaryLines = [];
    this.temporaryLabels = [];
    this.temporaryEndpoints = [];
    this.hoveredPoint = null;
  }

  getByID(id) {
    return this.measurements.find(measurement => measurement.id === id);
  }

  removeByID(id) {
    // Clear scene objects and remove from array
    let measurement = this.getByID(id);
    this.removeMeasurement(measurement);

    // Reset measurement volume/area values
    let measurementObject = this.measurementGroup.getByID(id);
    measurementObject.clear();
  }

  removeMeasurement(measurement: MeasureObject) {
    if (!measurement) return;

    this.removeObjects(measurement.lines);
    this.removeObjects(measurement.points);
    this.removeLabels(measurement.labels);

    this.measurements = this.measurements
      .filter(x => x.id !== measurement.id);
  }

  removeAll() {
    for (let measurement of this.measurements) {
      this.removeObjects(measurement.lines);
      this.removeObjects(measurement.points);
      this.removeLabels(measurement.labels);
    }

    this.measurements = [];
  }

  update(changeDetector: ChangeDetector) {
    if (!changeDetector.changed) return;

    this.temporaryEndpoints.forEach(mesh => {
      this.setPointScale(mesh);
    });

    this.measurements.forEach(measurement => {
      measurement.points.forEach(mesh => {
        this.setPointScale(mesh);
      });
    });
  }
}
